import React from 'react';
import {
  ProductInformationProps,
  StyledTextField,
} from '../productInformation';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useLcaProductCertificates } from 'src/Lca/hooks';
import { LcaProductCertificateOutSchema } from 'src/Lca/types';
import { toast } from 'react-toastify';
import { client } from 'src/utils/api-client';
import { useTranslation } from 'react-i18next';
import { buildSelectInputWithInfo } from '../fieldInfo';
import { ComplianceField } from 'src/Lca/compliance';

interface CertificationEntryProps {
  entry: Partial<LcaProductCertificateOutSchema>;
  regulatoryFrameworks?: string;
  onSaved?: () => void;
}

const documentTypeOptions = [
  {
    value: 'Regulatory compliance declarations',
    labelKey: 'documentTypes.regulatoryComplianceDeclarations',
  },
  {
    value: 'Environmental certificates',
    labelKey: 'documentTypes.environmentalCertificates',
  },
  {
    value: 'Sustainability & eco-labels',
    labelKey: 'documentTypes.sustainabilityEcoLabels',
  },
  {
    value: 'Materials & chemical compliance',
    labelKey: 'documentTypes.materialsChemicalCompliance',
  },
  {
    value: 'Quality & product standards',
    labelKey: 'documentTypes.qualityProductStandards',
  },
  {
    value: 'Manufacturing & supply chain certificates',
    labelKey: 'documentTypes.manufacturingSupplyChainCertificates',
  },
  {
    value: 'Circularity & end-of-life documentation',
    labelKey: 'documentTypes.circularityEndOfLifeDocumentation',
  },
  {
    value: 'Digital compliance',
    labelKey: 'documentTypes.digitalCompliance',
  },
  {
    value: 'Other',
    labelKey: 'documentTypes.other',
  },
];

function CertificationEntry({
  entry,
  regulatoryFrameworks,
  onSaved,
}: Readonly<CertificationEntryProps>) {
  const { t, i18n } = useTranslation('lca', { keyPrefix: 'certifications' });
  const { create, update, _delete } = useLcaProductCertificates({
    product_id: entry.product_id as number,
  });
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [form, setForm] = React.useState<
    Partial<LcaProductCertificateOutSchema>
  >({
    ...entry,
    name: entry.name || '',
    description: entry.description || '',
    is_environmental: entry.is_environmental ?? false,
    document_type: entry.document_type || '',
    valid_from: entry.valid_from || null,
    valid_to: entry.valid_to || null,
    is_valid_indefinitely: entry.is_valid_indefinitely ?? false,
    file_url: entry.file_url || null,
  });
  const [isModified, setIsModified] = React.useState(false);
  const [isUploadingFile, setIsUploadingFile] = React.useState(false);
  let uploadButtonLabel = t('uploadPdf');

  if (isUploadingFile) {
    uploadButtonLabel = t('uploading');
  } else if (!form.id) {
    uploadButtonLabel = t('uploadPdfDisabled');
  }

  const save = async () => {
    try {
      const payload = {
        product_id: form.product_id as number,
        name: form.name || '',
        description: form.description || '',
        is_environmental: Boolean(form.is_environmental),
        document_type: form.document_type || '',
        valid_from: form.valid_from || null,
        valid_to: form.valid_to || null,
        is_valid_indefinitely: Boolean(form.is_valid_indefinitely),
      };

      if (form.id === undefined) {
        const created = await create.mutateAsync(payload as any);
        setForm({
          ...(created as any),
          file_url: (created as any).file_url || null,
        });
      } else {
        const updated = await update.mutateAsync({
          ...payload,
          id: form.id,
        } as any);
        setForm({
          ...(updated as any),
          file_url: (updated as any).file_url || null,
        });
      }
      setIsModified(false);
      onSaved?.();
    } catch {
      toast.error(t('saveFailed') as string);
    }
  };

  const handleUploadFile = async (file?: File) => {
    if (!form.id || !file) {
      return;
    }

    try {
      setIsUploadingFile(true);
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await client.post<LcaProductCertificateOutSchema>(
        `/web/lca/certificates/${form.id}/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      setForm({ ...form, file_url: data.file_url || null });
      onSaved?.();
      toast.success(t('uploadSuccess') as string);
    } catch {
      toast.error(t('uploadFailed') as string);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleRemoveFile = async () => {
    if (!form.id) {
      return;
    }
    try {
      const { data } = await client.delete<LcaProductCertificateOutSchema>(
        `/web/lca/certificates/${form.id}/file`
      );
      setForm({ ...form, file_url: data.file_url || null });
      onSaved?.();
      toast.success(t('removeFileSuccess') as string);
    } catch {
      toast.error(t('removeFileFailed') as string);
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid #ececec',
        borderRadius: '12px',
        p: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <ComplianceField
        excelRow={39}
        regulatoryFrameworks={regulatoryFrameworks}
      >
        <FormControl fullWidth>
          <InputLabel id={`certificate-document-type-${form.id || 'new'}`}>
            {t('type')}
          </InputLabel>
          <Select
            labelId={`certificate-document-type-${form.id || 'new'}`}
            label={t('type')}
            input={buildSelectInputWithInfo({
              label: t('type'),
              title: i18n.t('lca:fieldInfoFallback', { field: t('type') }),
            })}
            value={form.document_type || ''}
            onChange={(event) => {
              setForm({ ...form, document_type: event.target.value });
              setIsModified(true);
            }}
          >
            {documentTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.labelKey)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </ComplianceField>

      <ComplianceField
        excelRow={40}
        regulatoryFrameworks={regulatoryFrameworks}
      >
        <StyledTextField
          fullWidth
          required
          label={t('name')}
          value={form.name || ''}
          onChange={(event) => {
            setForm({ ...form, name: event.target.value });
            setIsModified(true);
          }}
        />
      </ComplianceField>

      <ComplianceField
        excelRow={41}
        regulatoryFrameworks={regulatoryFrameworks}
      >
        <StyledTextField
          fullWidth
          minRows={3}
          multiline
          label={t('description')}
          value={form.description || ''}
          onChange={(event) => {
            setForm({ ...form, description: event.target.value });
            setIsModified(true);
          }}
        />
      </ComplianceField>

      <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <ComplianceField
          excelRow={42}
          regulatoryFrameworks={regulatoryFrameworks}
        >
          <StyledTextField
            label={t('validFrom')}
            type="date"
            value={form.valid_from || ''}
            onChange={(event) => {
              setForm({ ...form, valid_from: event.target.value || null });
              setIsModified(true);
            }}
            InputLabelProps={{ shrink: true }}
          />
        </ComplianceField>

        <ComplianceField
          excelRow={43}
          regulatoryFrameworks={regulatoryFrameworks}
        >
          <StyledTextField
            label={t('validTo')}
            type="date"
            disabled={Boolean(form.is_valid_indefinitely)}
            value={form.valid_to || ''}
            onChange={(event) => {
              setForm({ ...form, valid_to: event.target.value || null });
              setIsModified(true);
            }}
            InputLabelProps={{ shrink: true }}
          />
        </ComplianceField>
      </Box>

      <ComplianceField
        excelRow={44}
        regulatoryFrameworks={regulatoryFrameworks}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.is_valid_indefinitely)}
              onChange={(event) => {
                setForm({
                  ...form,
                  is_valid_indefinitely: event.target.checked,
                  valid_to: event.target.checked ? null : form.valid_to || null,
                });
                setIsModified(true);
              }}
            />
          }
          label={t('validIndefinitely')}
        />
      </ComplianceField>

      <ComplianceField
        excelRow={45}
        regulatoryFrameworks={regulatoryFrameworks}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(form.is_environmental)}
              onChange={(event) => {
                setForm({ ...form, is_environmental: event.target.checked });
                setIsModified(true);
              }}
            />
          }
          label={t('environmentalCertificate')}
        />
      </ComplianceField>

      <ComplianceField
        excelRow={47}
        regulatoryFrameworks={regulatoryFrameworks}
      >
        <Box>
          {form.file_url ? (
            <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Typography variant="body2">{t('pdfAttached')}</Typography>
              <Button
                variant="secondary"
                size="small"
                onClick={() => window.open(form.file_url || '', '_blank')}
              >
                {t('openPdf')}
              </Button>
              <Button
                variant="secondary"
                size="small"
                onClick={handleRemoveFile}
              >
                {t('removePdf')}
              </Button>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('noPdfAttached')}
            </Typography>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: 'none' }}
            onChange={(event) => {
              const file = event.target.files?.[0];
              handleUploadFile(file);
              event.target.value = '';
            }}
          />

          <Button
            variant="secondary"
            size="small"
            sx={{ mt: '8px' }}
            onClick={() => fileInputRef.current?.click()}
            disabled={!form.id || isUploadingFile}
          >
            {uploadButtonLabel}
          </Button>
          {!form.id && (
            <Typography
              variant="captionText"
              color="text.secondary"
              sx={{ display: 'block', mt: '8px' }}
            >
              {t('saveBeforeUpload')}
            </Typography>
          )}
        </Box>
      </ComplianceField>

      <Box sx={{ display: 'flex', gap: '12px' }}>
        <Button
          variant="secondary"
          size="small"
          onClick={save}
          disabled={!isModified}
        >
          {t('save')}
        </Button>
        {entry.id !== undefined && (
          <Button
            variant="secondary"
            size="small"
            onClick={() => _delete.mutate(entry.id as number)}
          >
            {t('delete')}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default function Certifications({ values }: ProductInformationProps) {
  const { t } = useTranslation('lca', { keyPrefix: 'certifications' });
  const { _data, refetch } = useLcaProductCertificates({
    product_id: values.id,
  });
  const [addNew, setAddNew] = React.useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {_data?.map((entry) => (
        <CertificationEntry
          key={entry.id}
          entry={entry}
          regulatoryFrameworks={values.regulatory_frameworks}
          onSaved={refetch}
        />
      ))}

      {addNew && (
        <CertificationEntry
          entry={{ product_id: values.id }}
          regulatoryFrameworks={values.regulatory_frameworks}
          onSaved={() => {
            setAddNew(false);
            refetch();
          }}
        />
      )}

      {!addNew && (
        <Button
          variant="secondary"
          size="small"
          onClick={() => setAddNew(true)}
        >
          {t('add')}
        </Button>
      )}
    </Box>
  );
}
